import { useMemo } from "react";
import "./MindMappingReportBTwelfthSection.css";

/* ── Goal ID → name map ──────────────────────────────────────── */
const GOAL_LOOKUP = {
  "1":"Learning & Knowledge","2":"Technology & Digital","3":"Work & Career",
  "4":"Money & Finance","5":"Health & Wellness","6":"Lifestyle",
  "7":"Social & Community","8":"Entertainment & Creativity","9":"Shopping",
  "10":"Problem-Solving","11":"Values & Beliefs","12":"Curiosity & Exploration",
};

/* ── Phase 1: VALIDATE (Day 1-30) — by sector type ──────────── */
const PHASE1 = {
  tertiary: (sector, state, goals, course) => [
    `Run 30 customer discovery interviews in ${state ?? "your city"} with your target users in the ${sector ?? "services"} space — ask about current tools, biggest frustrations, and monthly spend on the problem.`,
    `Map your top 10 direct competitors — build a comparison table of their pricing, features, and user reviews. Find 3 gaps none of them address.`,
    `Create a 1-page Problem-Solution document: what problem exists, who has it, why existing solutions fail. Share with 20 people for brutal feedback.`,
    `Define your North Star Metric (NSM) — the one number that proves you're solving a real problem. For SaaS: DAU, MRR. For marketplace: GMV. For EdTech: course completion rate.`,
    `Join 2 startup communities in ${state ?? "your city"} — attend 1 demo day, 1 founder meetup, or online cohort this month to build your early advisory network.`,
  ],
  secondary: (sector, state, goals, course) => [
    `Visit 5 manufacturing facilities or distribution centres in ${state ?? "your region"} to understand operational pain points firsthand — don't survey, observe.`,
    `Identify 3 potential anchor customers (distributors, OEMs, or large buyers) who would give you a letter of intent if you solve their specific problem.`,
    `Research PLI scheme eligibility for your ${sector ?? "manufacturing"} niche — list 3 grants or subsidies you can apply for in the next 90 days.`,
    `Map your full supply chain on paper: raw material → production → distribution → end customer. Mark every step with cost and bottleneck risk.`,
    `Get 1 industry expert or retired factory manager as an advisor — domain credibility accelerates B2B sales cycles significantly in manufacturing.`,
  ],
  primary: (sector, state, goals, course) => [
    `Spend 15 days embedded in your target district in ${state ?? "India"} — talk directly to 30 farmers, FPO leaders, or primary producers about their single biggest recurring problem.`,
    `Map the value chain from producer to consumer for your chosen commodity/crop — identify where the highest margin leakage occurs and which player in the chain earns the most.`,
    `Research RKVY-RAFTAAR, NABARD Rural Innovation Grant, and SFAC incubation programs — these can fund your first 12 months without equity dilution.`,
    `Identify 1 FPO (Farmer Producer Organisation) or cooperative as your first distribution partner — they represent hundreds of farmers and give you instant reach.`,
    `Build a simple WhatsApp-based solution or paper prototype first — validate that your target users will adopt it before building a full app.`,
  ],
};

/* ── Phase 2: BUILD (Day 31-60) — by fund level + MBTI ──────── */
function getPhase2(sector, state, affordability, mbtiCode, goals) {
  const isThinker = mbtiCode?.includes("T");
  const isJudger  = mbtiCode?.includes("J");
  const fundLow   = !affordability || affordability.toString().includes("0.5") || affordability.toString().includes("1-2");

  return [
    fundLow
      ? `Build an MVP using no-code tools (Bubble, Glide, Notion, WhatsApp) in under 4 weeks. Your goal is a working demo, not a perfect product — perfection is the enemy of validation.`
      : `Hire 1-2 engineers or a design partner to build your MVP. Use $5K–$15K budget maximum. Define an MVF (Minimum Viable Feature): the 1 feature without which the product has zero value.`,
    isJudger
      ? `Create a 30/60/90 day milestone dashboard — track customer conversations (target: 50), demo-to-signup conversion, and NPS score weekly. Treat these as non-negotiable milestones.`
      : `Set a weekly review cadence: every Friday, assess what you learned, what you shipped, and what you're changing. The goal is 3 learning cycles per week, not 3 features per week.`,
    isThinker
      ? `Build a financial model with unit economics: CAC, LTV, payback period, gross margin. Even if revenue is ₹0, know what these numbers SHOULD be at 100 customers.`
      : `Run 15 beta user sessions and capture qualitative feedback. Create a user journey map showing where users feel delight vs. friction. Fix the friction first.`,
    `Set up your first 10 paying customers before Month 2 ends. Even ₹499/month × 10 = ₹5K MRR — this is more powerful than any pitch deck in proving market demand.`,
    goals?.length > 0
      ? `Deep-dive into your primary goal area (${GOAL_LOOKUP[goals[0]] ?? "your chosen goal"}) — identify 3 specific startup ideas in this space and build an evidence file with market data, competitor screenshots, and customer quotes.`
      : `Build your evidence file: 5 customer quotes, 3 competitor screenshots, 1 market size data point, 1 regulatory risk note. This becomes your investor pitch foundation.`,
  ];
}

/* ── Phase 3: LAUNCH (Day 61-90) — by sector + MBTI + state ── */
function getPhase3(sector, state, mbtiCode, goals, sectorType, affordability) {
  const isExtravert = mbtiCode?.startsWith("E");
  const isTier1     = ["maharashtra","karnataka","delhi","tamil nadu","telangana","haryana","gujarat","andhra pradesh"].some(t => (state ?? "").toLowerCase().includes(t));

  return [
    isExtravert
      ? `Apply to DPIIT Startup India recognition (free, takes 2 weeks) and pitch at 1 startup event in ${state ?? "your city"} this month. Your extroverted energy is a fundraising asset — use it live.`
      : `File your DPIIT Startup India recognition online. Prepare a 3-minute demo video of your product solving a real customer's problem. Post it on LinkedIn — it does the "pitch" without you being in the room.`,
    isTier1
      ? `Apply to 2 accelerator programs based in ${state ?? "your state"}: YC India, Antler, T-Hub, Sequoia Surge, NASSCOM 10K Startups, or relevant sector-specific programs. Deadlines are quarterly — apply now.`
      : `Apply to Atal Incubation Centers (AIC) and DPIIT state incubation programs in ${state ?? "your state"}. These provide ₹25L–₹1 Cr in non-dilutive funding — much faster than VC for early stage.`,
    `Get your first press mention or social proof: a case study from one happy customer, a blog post about the problem you solve, or a LinkedIn post with real data. Distribution builds investor FOMO.`,
    sector
      ? `Attend 1 industry-specific event in the ${sector} space — an expo, trade show, or B2B conference. Your target customers and potential investors both attend these.`
      : `Attend 1 industry meetup or sector-specific event. Face-to-face conversations compress relationship-building timelines that would otherwise take months on email.`,
    `Prepare your fundraising pitch deck (10 slides max): Problem → Solution → Market → Traction → Team → Ask. Send to 10 angels in your sector. Use warm introductions from your community contacts built in Phase 1.`,
  ];
}

/* ── MBTI helper ─────────────────────────────────────────────── */
function buildMbti(res) {
  const LETTER = { Extravert:"E",Introvert:"I",Thinking:"T",Feeling:"F",Judging:"J",Perceiving:"P",Intuition:"N",Sensing:"S" };
  const e = LETTER[res?.trait_intro_extro]    ?? "?";
  const s = LETTER[res?.trait_sense_intuit]   ?? "?";
  const t = LETTER[res?.trait_think_feel]     ?? "?";
  const j = LETTER[res?.trait_judge_perceive] ?? "?";
  return `${e}${s}${t}${j}`;
}

const PHASE_CONFIG = [
  { key:"p1", label:"DAY 1 – 30", theme:"Validate",  accent:"#0077d6", bg:"#eff6ff", border:"#bfdbfe" },
  { key:"p2", label:"DAY 31 – 60",theme:"Build",     accent:"#7c3aed", bg:"#faf5ff", border:"#ddd6fe" },
  { key:"p3", label:"DAY 61 – 90",theme:"Launch",    accent:"#059669", bg:"#f0fdf4", border:"#bbf7d0" },
];

const MindMappingReportBTwelfthSection = ({ res, toggle }) => {
  const sectorType = (res?.sector_type ?? "tertiary").toLowerCase();
  const sector     = res?.sector  ?? "";
  const state      = res?.state   ?? "";
  const goals      = res?.goals   ?? [];
  const mbtiCode   = buildMbti(res);

  const phases = useMemo(() => {
    const p1fn = PHASE1[sectorType] ?? PHASE1.tertiary;
    return [
      p1fn(sector, state, goals, res?.course),
      getPhase2(sector, state, res?.affordability, mbtiCode, goals),
      getPhase3(sector, state, mbtiCode, goals, sectorType, res?.affordability),
    ];
  }, [sectorType, sector, state, goals, mbtiCode, res?.affordability, res?.course]);

  return (
    <div className="mm-rb-twelfth-container">
      <div className={toggle ? "table-adjust" : "mm-rb-twelfth-final-table"}>
        <span className="mm-rb-twelfth-corner mm-rb-twelfth-corner-top-left" />
        <span className="mm-rb-twelfth-corner mm-rb-twelfth-corner-top-right" />
        <span className="mm-rb-twelfth-corner mm-rb-twelfth-corner-bottom-left" />
        <span className="mm-rb-twelfth-corner mm-rb-twelfth-corner-bottom-right" />

        <div className="mm-rb-twelfth-module-title">
          <span className="module-prefix">Module 12:</span> 90-Day Personalised Milestone Roadmap
          <span className="mm-rb-premium-tag">★ Report A+ Exclusive</span>
        </div>

        <div className="mm-rb-twelfth-context-bar">
          Personalised for: <strong>{sector || (sectorType + " sector")}</strong>
          {state && <> · <strong>{state}</strong></>}
          · Founder archetype: <strong>{mbtiCode}</strong>
          {goals.length > 0 && <> · {goals.length} goals mapped</>}
        </div>

        <div className="mm-rb-twelfth-phases-grid">
          {PHASE_CONFIG.map((cfg, pi) => (
            <div
              key={cfg.key}
              className="mm-rb-twelfth-phase-card"
              style={{ background: cfg.bg, borderColor: cfg.border }}
            >
              <div className="mm-rb-twelfth-phase-label" style={{ color: cfg.accent }}>
                {cfg.label}
              </div>
              <div className="mm-rb-twelfth-phase-theme" style={{ color: cfg.accent }}>
                {cfg.theme}
              </div>
              <div className="mm-rb-twelfth-divider" style={{ background: cfg.border }} />
              <ul className="mm-rb-twelfth-phase-list">
                {phases[pi].map((action, ai) => (
                  <li key={ai} className="mm-rb-twelfth-phase-item">
                    <span className="mm-rb-twelfth-dot" style={{ background: cfg.accent }} />
                    <span>{action}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mm-rb-twelfth-footer">
          © MarsMate AI · This roadmap is generated from your profile — sector, location, personality archetype ({mbtiCode}), and goals.
          Treat each phase as a sprint with weekly check-ins.
        </div>
      </div>
    </div>
  );
};

export default MindMappingReportBTwelfthSection;
